import React, { useEffect, useCallback, useState, useRef } from "react";
import { motion, AnimatePresence, useDragControls } from "motion/react";
import { X } from "lucide-react";

import { usePreviewPanel } from "../../contexts/PreviewPanelContext";
import useIsMobile from "../../hooks/useIsMobile";

import ProductGridView from "./ProductGridView";
import ProductDetailView from "./ProductDetailView";
import ComparisonView from "./ComparisonView";

// Selector for focusable elements
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * PreviewPanel - Slide-in panel for product details, grid view, and comparisons
 *
 * Desktop: Slides in from right
 * Mobile: Slides up as bottom sheet (to be enhanced in US6)
 */
export default function PreviewPanel() {
  const { isOpen, contentType, products, openGrid, close } = usePreviewPanel();
  const isMobile = useIsMobile();
  const dragControls = useDragControls();
  const [dragY, setDragY] = useState(0);
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Handle escape key and focus trap
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === "Escape") {
      close();
      return;
    }

    // Focus trap on Tab
    if (e.key === "Tab" && panelRef.current) {
      const focusableElements = panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, [isOpen, close]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Focus management: save previous focus, focus first element when open, restore on close
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      // Focus the close button after animation settles
      const timer = setTimeout(() => {
        const firstFocusable = panelRef.current?.querySelector(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle drag end for swipe-to-dismiss
  const handleDragEnd = useCallback((event, info) => {
    // If dragged down more than 100px or with enough velocity, close the panel
    if (info.offset.y > 100 || info.velocity.y > 500) {
      close();
    }
    setDragY(0);
  }, [close]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/30 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
            aria-hidden="true"
          />

          {/* Panel */}
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={
              contentType === "product" ? "Product Details" :
              contentType === "grid" ? "All Products" :
              contentType === "compare" ? "Compare Products" : "Preview Panel"
            }
            className={`
              fixed z-50 bg-white shadow-xl
              ${isMobile
                ? "inset-x-0 bottom-0 rounded-t-2xl max-h-[85vh]"
                : "top-0 right-0 h-full w-full max-w-md md:max-w-lg lg:max-w-xl"
              }
            `}
            initial={isMobile ? { y: "100%" } : { x: "100%" }}
            animate={isMobile ? { y: dragY } : { x: 0 }}
            exit={isMobile ? { y: "100%" } : { x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            // Mobile swipe-to-dismiss
            drag={isMobile ? "y" : false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDrag={(e, info) => setDragY(Math.max(0, info.offset.y))}
            onDragEnd={handleDragEnd}
          >
            {/* Header with close button */}
            <div className="sticky top-0 z-10 bg-white border-b border-neutral-100 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-medium text-penta-black">
                {contentType === "product" && "Product Details"}
                {contentType === "grid" && "All Products"}
                {contentType === "compare" && "Compare Products"}
              </h2>
              <button
                onClick={close}
                className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
                aria-label="Close panel"
              >
                <X size={20} className="text-penta-grey-medium" />
              </button>
            </div>

            {/* Mobile drag handle */}
            {isMobile && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-neutral-300 rounded-full" />
            )}

            {/* Content */}
            <div className={`overflow-y-auto ${isMobile ? "max-h-[calc(85vh-60px)]" : "h-[calc(100%-60px)]"}`}>
              {contentType === "grid" && <ProductGridView products={products} />}
              {contentType === "product" && (
                <ProductDetailView
                  onBack={products?.length > 0 ? () => openGrid(products, 'All Products') : null}
                />
              )}
              {contentType === "compare" && <ComparisonView />}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
